import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Globe, Users, Lock, User, ThumbsUp, ThumbsDown, UserCheck, Handshake, FileEdit, Archive, CalendarCheck, Clock, PlayCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * Standardized badge styles for trips, motorcycles, and groups
 * REGRAS (ver DESIGN_SYSTEM.md secção 8.1):
 * - Todos do mesmo tamanho (px-2.5 py-1 text-xs)
 * - Todos rectangulares (rounded-md)
 * - Todos com tooltips traduzidas
 * - Nenhum deve ter cursor-help (usar cursor-default)
 */

// Badge style configurations - high contrast with solid backgrounds
const BADGE_STYLES = {
  // Visibility badges (used by motorcycles, garage, clubs — NOT trips)
  visibility: {
    public: "bg-green-500/80 text-white border-green-400",
    participants: "bg-cyan-500/80 text-white border-cyan-400",
    participants_clubs: "bg-blue-500/80 text-white border-blue-400",
    collaborators: "bg-purple-500/80 text-white border-purple-400",
    private: "bg-orange-500/80 text-white border-orange-400"
  },
  // Context badges
  context: {
    personal: "bg-blue-500/80 text-white border-blue-400",
    group: "bg-green-500/80 text-white border-green-400"
  },
  // Data completeness badges
  data: {
    ok: "bg-green-500/80 text-white border-green-400",
    incomplete: "bg-red-500/80 text-white border-red-400"
  },
  // Publication status badges (NEW — replaces visibility for trips)
  publication: {
    draft: "bg-zinc-500/80 text-white border-zinc-400",
    active: "bg-green-500/80 text-white border-green-400",
    closed: "bg-amber-500/80 text-white border-amber-400",
    archived: "bg-slate-600/80 text-white border-slate-500"
  },
  // Planning status badges (NEW — replaces temporal for planned trips)
  planning: {
    enrollment: "bg-cyan-500/80 text-white border-cyan-400",
    waiting: "bg-amber-500/80 text-white border-amber-400",
    ongoing: "bg-blue-500/80 text-white border-blue-400"
  }
};

// Base badge class - PADRONIZADO (ver DESIGN_SYSTEM.md secção 8.1)
const BASE_BADGE_CLASS = "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold cursor-default shadow-sm";

/**
 * Visibility Badge Component
 * Supports all visibility types: public, participants, participants_clubs, collaborators, private
 */
export function VisibilityBadge({ visibility, showTooltip = true }) {
  const { t } = useTranslation("common");
  
  // Use unified translation keys under badges.visibility
  const config = {
    public: { 
      icon: Globe, 
      label: t("badges.visibility.public"),
      tooltip: t("badges.visibility.public_tooltip")
    },
    participants: { 
      icon: UserCheck, 
      label: t("badges.visibility.participants"),
      tooltip: t("badges.visibility.participants_tooltip")
    },
    participants_clubs: { 
      icon: Users, 
      label: t("badges.visibility.participants_clubs"),
      tooltip: t("badges.visibility.participants_clubs_tooltip")
    },
    collaborators: { 
      icon: Handshake, 
      label: t("badges.visibility.collaborators"),
      tooltip: t("badges.visibility.collaborators_tooltip")
    },
    private: { 
      icon: Lock, 
      label: t("badges.visibility.private"),
      tooltip: t("badges.visibility.private_tooltip")
    }
  };
  
  const current = config[visibility] || config.private;
  const Icon = current.icon;
  const style = BADGE_STYLES.visibility[visibility] || BADGE_STYLES.visibility.private;
  
  const badge = (
    <Badge 
      variant="outline" 
      className={`${BASE_BADGE_CLASS} ${style}`}
      data-testid="visibility-badge"
    >
      <Icon className="h-3.5 w-3.5" />
      {current.label}
    </Badge>
  );
  
  if (!showTooltip) return badge;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent>
          <p>{current.tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Context Badge Component (Personal/Group)
 */
export function ContextBadge({ context, clubName, showTooltip = true }) {
  const { t } = useTranslation("common");
  
  const isClub = context === "club";
  const Icon = isClub ? Users : User;
  const label = isClub ? (clubName || t("badges.context.group")) : t("badges.context.personal");
  const tooltip = isClub 
    ? t("badges.context.group_tooltip")
    : t("badges.context.personal_tooltip");
  const style = isClub ? BADGE_STYLES.context.group : BADGE_STYLES.context.personal;
  
  const badge = (
    <Badge 
      variant="outline" 
      className={`${BASE_BADGE_CLASS} ${style}`}
      data-testid="context-badge"
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </Badge>
  );
  
  if (!showTooltip) return badge;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent>
          <p>{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Data Completeness Badge Component
 */
export function DataBadge({ dataOk, showTooltip = true }) {
  const { t } = useTranslation("common");
  
  const isComplete = dataOk !== false;
  const Icon = isComplete ? ThumbsUp : ThumbsDown;
  const label = t("badges.data.label");
  const tooltip = isComplete 
    ? t("badges.data.ok_tooltip")
    : t("badges.data.incomplete_tooltip");
  const style = isComplete ? BADGE_STYLES.data.ok : BADGE_STYLES.data.incomplete;
  
  const badge = (
    <Badge 
      variant="outline" 
      className={`${BASE_BADGE_CLASS} ${style}`}
      data-testid="data-ok-badge"
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </Badge>
  );
  
  if (!showTooltip) return badge;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent>
          <p>{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Publication Status Badge Component (Draft/Active/Closed/Archived)
 * Replaces VisibilityBadge for trips in the new state machine.
 */
export function PublicationStatusBadge({ status, showTooltip = true }) {
  const { t } = useTranslation("common");
  
  const config = {
    draft: { 
      icon: FileEdit, 
      label: t("badges.publication_status.draft", "Rascunho"),
      tooltip: t("badges.publication_status.draft_tooltip", "Em preparação")
    },
    active: { 
      icon: Globe, 
      label: t("badges.publication_status.active", "Ativo"),
      tooltip: t("badges.publication_status.active_tooltip", "Visível a todos")
    },
    closed: { 
      icon: Lock, 
      label: t("badges.publication_status.closed", "Fechado"),
      tooltip: t("badges.publication_status.closed_tooltip", "Oculto do público")
    },
    archived: { 
      icon: Archive, 
      label: t("badges.publication_status.archived", "Arquivado"),
      tooltip: t("badges.publication_status.archived_tooltip", "Passeio arquivado")
    }
  };
  
  const current = config[status] || config.draft;
  const Icon = current.icon;
  const style = BADGE_STYLES.publication[status] || BADGE_STYLES.publication.draft;
  
  const badge = (
    <Badge 
      variant="outline" 
      className={`${BASE_BADGE_CLASS} ${style}`}
      data-testid="publication-status-badge"
    >
      <Icon className="h-3.5 w-3.5" />
      {current.label}
    </Badge>
  );
  
  if (!showTooltip) return badge;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent>
          <p>{current.tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Planning Status Badge Component (Enrollment/Waiting/Ongoing)
 * Only shown for planned trips that are active.
 */
export function PlanningStatusBadge({ planningStatus, showTooltip = true }) {
  const { t } = useTranslation("common");
  
  if (!planningStatus || planningStatus === "past_dates") return null;
  
  const config = {
    enrollment: { 
      icon: CalendarCheck, 
      label: t("badges.planning_status.enrollment", "Adesão Disponível"),
      tooltip: t("badges.planning_status.enrollment_tooltip", "Período de adesão aberto")
    },
    waiting: { 
      icon: Clock, 
      label: t("badges.planning_status.waiting", "Planeado"),
      tooltip: t("badges.planning_status.waiting_tooltip", "Passeio planeado")
    },
    ongoing: { 
      icon: PlayCircle, 
      label: t("badges.planning_status.ongoing", "A Decorrer"),
      tooltip: t("badges.planning_status.ongoing_tooltip", "Passeio a decorrer")
    }
  };
  
  const current = config[planningStatus];
  if (!current) return null;
  
  const Icon = current.icon;
  const style = BADGE_STYLES.planning[planningStatus] || BADGE_STYLES.planning.waiting;
  
  const badge = (
    <Badge 
      variant="outline" 
      className={`${BASE_BADGE_CLASS} ${style}`}
      data-testid="planning-status-badge"
    >
      <Icon className="h-3.5 w-3.5" />
      {current.label}
    </Badge>
  );
  
  if (!showTooltip) return badge;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent>
          <p>{current.tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * All Badges Combined Component
 * For use in trip cards and detail pages
 */
export function TripBadges({ 
  publicationStatus,
  planningStatus,
  dataOk, 
  context = "personal",
  clubName,
  showPublicationStatus = true,
  showPlanningStatus = true,
  showData = true,
  showContext = false,
  showTooltips = true
}) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {showContext && (
        <ContextBadge 
          context={context} 
          clubName={clubName}
          showTooltip={showTooltips} 
        />
      )}
      {showPublicationStatus && publicationStatus && (
        <PublicationStatusBadge 
          status={publicationStatus} 
          showTooltip={showTooltips} 
        />
      )}
      {showPlanningStatus && planningStatus && (
        <PlanningStatusBadge 
          planningStatus={planningStatus} 
          showTooltip={showTooltips} 
        />
      )}
      {showData && (
        <DataBadge 
          dataOk={dataOk} 
          showTooltip={showTooltips} 
        />
      )}
    </div>
  );
}

// Export styles for external use
export { BADGE_STYLES, BASE_BADGE_CLASS };
